/**
 * SNS Post Setup
 *
 * Post-level SNS glue.
 * Shared wiring for exposing handlers, auto-detecting SNS links from content (without modifying content),
 * and performing initial preview sync.
 */

import { WysiwygUtils } from "../../wysiwyg/wysiwyg-utils";
import { SNSPostListener } from "./sns-post-listener";

type SNSExposedHandler = {
    updatePreviews?: () => void;
    handleSnsAdd?: (() => void) | ((ev: Event) => void);
    handleSnsInput?: (() => void) | ((ev: Event) => void);
};

/**
 * Expose handler for SNS links panel UI controller.
 */
function exposeHandler(key: string, handler: SNSExposedHandler): void {
    const existing = Reflect.get(globalThis, "__snsLinksHandlers");
    if (!existing || typeof existing !== "object") {
        Reflect.set(globalThis, "__snsLinksHandlers", {});
    }
    const map = Reflect.get(globalThis, "__snsLinksHandlers");
    if (!map || typeof map !== "object") {
        return;
    }
    Reflect.set(map, String(key), handler);
}

type SNSPostSetupRegisterParams = {
    postKey: string;
    rootEl: Document | HTMLElement;
    handler: SNSExposedHandler;
};

type SNSPostSetupWireParams = {
    postKey: string;
    formRoot: HTMLElement | Document;
    handler: SNSExposedHandler & { updatePreviews: () => void };
    elements: { contentTextarea?: HTMLTextAreaElement | null };
    initialSyncDelayMs?: number;
};

export class SNSPostSetup {
    /**
     * Register listeners related to sns_links[] inputs.
     * (No separate Listener class; keep wiring local here.)
     */
    public static registerListeners(params: SNSPostSetupRegisterParams): void {
        SNSPostListener.registerSnsLinksListeners(params);
    }

    /**
     * Wire SNS links manager common behavior.
     *
     * Requirements:
     * - Never auto-modify editor content.
     * - Only update sns_links[] via WysiwygUtils.mergeSnsLinksIntoEditableList.
     */
    public static wireSnsLinksManager(params: SNSPostSetupWireParams): void {
        const { postKey, formRoot, handler, elements, initialSyncDelayMs = 100 } = params;

        exposeHandler(postKey, handler);

        const contentTextarea = elements?.contentTextarea || null;
        if (contentTextarea) {
            SNSPostListener.registerContentTextareaAutoDetect({
                postKey,
                formRoot: formRoot || document,
                contentTextarea,
                debounceMs: 300,
                onDebouncedInput: () => {
                    const urls = WysiwygUtils.detectSnsUrlsFromText(contentTextarea.value || "");
                    if (!urls || urls.length === 0) return;
                    WysiwygUtils.mergeSnsLinksIntoEditableList(contentTextarea, urls);
                },
            });
        }

        // Initial preview sync (in case old data or back navigation)
        setTimeout(() => {
            try {
                handler.updatePreviews();
            } catch (error) {
                console.warn("[SNS Post] Initial preview sync failed", error);
            }
        }, initialSyncDelayMs);
    }
}
